import {
    isEmpty,
    cloneDeep,
} from 'lodash';
import { v4 as uuid } from 'uuid';
import { renderUpdates } from '../../../components/ImageEditor/engineRenderer';
import { dispatchChangeEvent } from '../../../components/ImageEditor/engineListeners';

import {
    createImage,
    toDataURI,
} from '../../../utils/image';

/*
 * Logo Full States Schema
 * activeEditorState
 * editorUI
 * styleData
 */

const LOGO_MAX_STATE = 20;

// method actions
function undoLogoState () {
    this.undoRedoToggled = true;
    if (this.logoStateIndex - 1 <= 0) {
        this.logoStateIndex = 0;
    } else {
        this.logoStateIndex -= 1;
    }
    this.toggleUndo();
    this.toggleRedo();
    this.restoreActiveState();
    dispatchChangeEvent();
}

function redoLogoState () {
    this.undoRedoToggled = true;
    if (this.logoStateIndex + 1 >= this.logoStates.length - 1) {
        this.logoStateIndex = this.logoStates.length - 1;
    } else {
        this.logoStateIndex += 1;
    }
    this.toggleUndo();
    this.toggleRedo();
    this.restoreActiveState();
    dispatchChangeEvent();
}

function toggleUndo () {
    if (this.logoStateIndex <= 0) {
        this.undoEnable = false;
    } else {
        this.undoEnable = true;
    }
    this.observed.undoEnable = uuid();
}

function toggleRedo () {
    if (this.logoStateIndex >= this.logoStates.length - 1) {
        this.redoEnable = false;
    } else {
        this.redoEnable = true;
    }
    this.observed.redoEnable = uuid();
}

function pushLogoEditorActiveStates () {
    clearTimeout(this.undoDebounceTO);
    const astate = {
        stylesData: this.stylesData,
        activeEditorState: cloneDeep(window.currentEditorState),
        logoImageURI: this.logoImageURI,
        isImageBrowsed: this.isImageBrowsed,
        imageAsDefault: this.imageAsDefault,
        transformChanged: this.transformChanged,
    };
    const self = this;
    this.undoDebounceTO = setTimeout(() => {
        self.pushLogoState(astate);
    }, 200);
}

function pushLogoState (newState) {
    let currentState = {};
    if (isEmpty(this.logoStates)) {
        this.logoStates = [];
        currentState = {};
    }

    if (this.logoStates.length > 0) {
        currentState = this.logoStates[this.logoStates.length - 1];
    }

    // clone deep ensure that all states didn't reference other state
    const finalState = {
        ...cloneDeep(currentState),
        ...cloneDeep(newState),
    };

    if (this.logoStateIndex !== this.logoStates.length - 1 && this.logoStates.length > 0) {
        // it means that user undo
        // will push a new set of state  which means the old states will be removed.
        this.logoStates.splice(this.logoStateIndex + 1);
    }

    this.logoStates.push(finalState); // instead of push it needs to be inserted to the current state
    this.reduceLogoStateToMax();
    if (!this.undoRedoInitialized) {
        this.undoRedoInitialized = true;
        // this.logoStates.shift();
    }
    this.logoStateIndex = this.logoStates.length - 1;
    this.toggleUndo();
    this.toggleRedo();
    /*
     * Don't remove this log comments for faster debugging.
     * console.log('pushLogoState()  finalState: ', finalState);
     * console.log('pushLogoState()  this.logoStateIndex: ', this.logoStateIndex);
     * console.log('pushLogoState()  this.logoStates: ', this.logoStates);
     */
}

function reduceLogoStateToMax () {
    // remove the first element state list based on max
    if (this.logoStates.length > LOGO_MAX_STATE) {
        this.logoStates.shift();
    }
}

// restore states and render it on screen

function restoreActiveState () {
    /*
     * Don't remove this log comments for faster debugging.
     * console.log('restoreActiveState()  this.logoStateIndex: ', this.logoStateIndex);
     * console.log('restoreActiveState()  this.logoStates: ', this.logoStates);
     */
    const state = cloneDeep(this.logoStates[this.logoStateIndex]);
    this.stylesData = state.stylesData;
    this.isImageBrowsed = state.isImageBrowsed;
    this.logoImageURI = state.logoImageURI;
    this.imageAsDefault = state.imageAsDefault;
    this.transformChanged = state.transformChanged;
    this.observed.logoImageURI = uuid();
    window.activeEditorState = state.activeEditorState;
    window.editorState = cloneDeep(window.activeEditorState);
    // window.editorUI = state.editorUI;
    // restore state of logo, url, shadow, transparent
    if (typeof this.setStylesDataState === 'function') {
        this.undoStateUpdate = true;
        this.setStylesDataState({
            ...this.stylesData,
        });
    }
}

function reflectEditorStateUpdates () {
    // restore logo editor engine state
     if (this.undoStateUpdate) {
        if (!isEmpty(window.activeEditorState)) {
            renderUpdates();
            dispatchChangeEvent();
        }
        clearTimeout(this.undoFlagTO);
        this.undoFlagTO = setTimeout(() => {
            this.undoStateUpdate = false;
        }, 400);
    }
}

function setStylesDataUpdater (f) {
    this.setStylesDataState = f;
    this.initUndoRedo();
}

function initUndoRedo () {
    this.undoRedoInitialized = false;
    this.logoStates = [];
    this.toggleUndo();
    this.toggleRedo();
}

function setLogoImageURI (imageURI) {
    this.logoImageURI = imageURI;
}

function setIsImageBrowsed (v) {
    this.isImageBrowsed = v;
}

/*
 * invoked at useImageCrop.updateImageByDefaults() to validate whether to push the state to the stack or not.
 */

function validatePushOnUndoRedoEvents () {
    if (!this.undoRedoToggled) {
        this.setTransformChanged(false);
        this.pushLogoEditorActiveStates();
    } else {
        this.undoRedoToggled = false;
    }
}

/*
 * use in useModuleInput updateSavable(), validate whether to push a state on undo/redo stack.
 */
function shouldSkipStackPush () {
    let skip = false;
    if (this.stateChangeTarget === 'logoShadow' ||
        this.stateChangeTarget === 'logoTransparent' ||
        this.stateChangeTarget === 'logoImageShapeDefault'
    ) {
            skip = true;
            this.stateChangeTarget = null;
    }
    return skip;
}

async function getImageDataURI (url) {
    try {
      const obj = await createImage(url);
      const dataURI = toDataURI(obj.image);
      return dataURI;
    } catch (error) {
      console.error('Failed to get image data URI:', error);
      return null;
    }
}

/*
 * the flag is used in useImageEditor to validate if editorState will initialize or not.
 * invoked in useImageCrop updateImageByDefaults().
 */
function setImageAsDefault (v) {
    this.imageAsDefault = v;
}

/*
 * this helper function validate whether editor state will initialize when editor imageSource state changes.
 */

function shouldInitEditorStateToDefault () {
    if ((this.imageAsDefault && !this.transformChanged) ||
        !this.undoStateUpdate) {
        return true;
    }
    return false;
}

function setTransformChanged (v) {
    this.transformChanged = v;
}

const undoMethods = {
    undoLogoState,
    redoLogoState,
    pushLogoState,
    reduceLogoStateToMax,
    restoreActiveState,
    pushLogoEditorActiveStates,
    toggleUndo,
    toggleRedo,
    setStylesDataUpdater,
    initUndoRedo,
    setLogoImageURI,
    validatePushOnUndoRedoEvents,
    reflectEditorStateUpdates,
    setIsImageBrowsed,
    getImageDataURI,
    setImageAsDefault,
    setTransformChanged,
    shouldSkipStackPush,
    shouldInitEditorStateToDefault,
};

export default undoMethods;
